import React, { Fragment, useState } from "react";
import CustomAudioPlayer from "../../ReuseableComponents/CustomPlayer";
import CustomVideoPlayer from "../../ReuseableComponents/CustomVideoPlayer";
import {
  CodeBracketIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Tab, Dialog, Transition } from "@headlessui/react";

// Feature flag to control normalized blocks usage
// When true: uses normalized_block data (new format) with legacy fallback
// When false: uses legacy blocks only
const USE_NORMALIZED_BLOCKS = true;

function EmbedClass({ classData }) {
  const [open, setOpen] = useState(false);
  const [mediaData, setMediaData] = useState([]);
  const [startHereTime, setStartHereTime] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [checked, setChecked] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [copyDisabled, setCopyDisabled] = useState(false);
  const [copyStatus, setCopyStatus] = useState({
    noCopy: true,
    copySuccess: false,
    copyFail: false,
  });

  function openModal() {
    handleModalState();
    handleMediaData();
  }

  function handleModalState() {
    setOpen(true);
    setStartHereTime(0);
    setCurrentTime(0);
    setChecked(false);
    setCopyDisabled(false);
    setCopyStatus({ noCopy: true, copySuccess: false, copyFail: false });
  }

  function handleMediaData() {
    const mediaData = getMediaData();
    const mediaDataWithEmbedCode = addEmbedCodeToMediaData({ mediaData });
    setMediaData(mediaDataWithEmbedCode);
  }

  const getMediaData = () => {
    // Determine which block format to use
    const useNormalized =
      USE_NORMALIZED_BLOCKS &&
      Array.isArray(classData?.normalized_block) &&
      classData.normalized_block.length > 0;

    if (useNormalized) {
      return getMediaDataFromNormalizedBlocks();
    } else {
      return getMediaDataFromLegacyBlocks();
    }
  };

  // Extract media data from normalized blocks (new format)
  const getMediaDataFromNormalizedBlocks = () => {
    let mediaData = [];
    const sortedBlocks = [...classData.normalized_block].sort(
      (a, b) => a.position - b.position
    );

    for (let i = 0; i < sortedBlocks.length; i++) {
      const { block_type, block, id } = sortedBlocks[i];

      // Handle HTML blocks - extract embedded audio/video tags
      if (block_type === "html" && block?.html_block) {
        const extractedMedia = extractMediaData({
          htmlString: block.html_block,
          blockId: id,
        });
        if (extractedMedia.length > 0) {
          mediaData = extractedMedia;
          break;
        }
      }

      // Handle direct audio blocks
      if (block_type === "audio" && block?.audio_src) {
        mediaData.push({
          id: `Audio-${i}`,
          blockId: id,
          type: "Audio",
          url: block.audio_src,
        });
      }

      // Handle direct video blocks
      if (block_type === "video" && block?.video_src) {
        mediaData.push({
          id: `Video-${i}`,
          blockId: id,
          type: "Video",
          url: block.video_src,
        });
      }
    }

    return mediaData;
  };

  // Extract media data from legacy blocks (fallback)
  const getMediaDataFromLegacyBlocks = () => {
    let mediaData = [];
    // Support both 'blocks' and 'block' field names for legacy format
    const legacyBlocks = classData?.blocks || classData?.block || [];

    for (let i = 0; i < legacyBlocks.length; i++) {
      if (legacyBlocks[i].type === "Custom HTML") {
        // Function to get type and url from the html string
        mediaData = extractMediaData({
          htmlString: legacyBlocks[i].html,
          blockId: legacyBlocks[i].id,
        });
        break;
      }
      if (
        legacyBlocks[i].type === "Audio" ||
        legacyBlocks[i].type === "Video"
      ) {
        mediaData.push({
          id: `${legacyBlocks[i].type}-${i}`,
          blockId: legacyBlocks[i].id,
          type: legacyBlocks[i].type,
          url: legacyBlocks[i].url,
        });
      }
    }
    return mediaData;
  };

  const extractMediaData = ({ htmlString, blockId }) => {
    // Parse the HTML string into a DOM object
    const extractedData = [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    const videoTags = doc.querySelectorAll("video");
    if (videoTags.length) {
      videoTags.forEach((videoTag, index) => {
        const videoUrl = videoTag.getAttribute("src");
        extractedData.push({
          id: `video-${index}`,
          blockId: blockId,
          url: videoUrl,
          type: "Video",
        });
      });
    }
    const audioTags = doc.querySelectorAll("audio");
    if (audioTags.length) {
      audioTags.forEach((audioTag, index) => {
        const audioUrl = audioTag.getAttribute("src");
        extractedData.push({
          id: `index-${index}`,
          blockId: blockId,
          url: audioUrl,
          type: "Audio",
        });
      });
    }
    return extractedData;
  };

  const addEmbedCodeToMediaData = ({ mediaData, startHere = "" }) => {
    const mediaDataWithEmbedCode = mediaData.map((media) => ({
      ...media,
      embedCode: getEmbedCode({
        url: media.url,
        type: media.type,
        start: startHere,
      }),
    }));
    return mediaDataWithEmbedCode;
  };

  const getEmbedCode = ({ url, type, start }) => {
    let embedCode = generateEmbedCode({ url, type, start });
    if (!embedCode) {
      embedCode = "Embed code failed to generate";
      setCopyDisabled(true);
    }
    return embedCode;
  };

  const generateEmbedCode = ({ url, type, start }) => {
    let embeddableCode;
    if (type === "Video") {
      embeddableCode = `<iframe title="embed-video-${window.location.pathname.slice(
        1
      )}" src="${window.location.origin}/embed/video${
        window.location.pathname
      }?url=${url}${
        start ? `&t=${start}` : ""
      }" width="560" height="455" frameborder="0" style="width: 560px; height: 455px; border: none" allow="autoplay"  allowFullScreen ></iframe>`;
    }
    if (type === "Audio") {
      embeddableCode = `<iframe title="embed-audio-${window.location.pathname.slice(
        1
      )}" src="${window.location.origin}/embed/audio${
        window.location.pathname
      }?url=${url}${
        start ? `&t=${start}` : ""
      }" width="100%" height="140" frameborder="0" style="width: 100%; height: 140px; border: none" allow="autoplay"></iframe>`;
    }
    return embeddableCode;
  };

  const handleCopy = async (embedCode) => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopyStatus((prev) => ({ ...prev, copySuccess: true }));
    } catch (error) {
      console.log(error);
      setCopyStatus((prev) => ({ ...prev, copyFail: true }));
    }
  };

  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  function handleStartHereTime(currentTime) {
    setCurrentTime(currentTime);
    setStartHereTime(checked ? currentTime : 0);
  }

  const formatTime = (time) => {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);
    return `${
      hours
        ? `${String(hours)}:${String(minutes).padStart(2, "0")}`
        : `${String(minutes)}`
    }:${String(seconds).padStart(2, "0")}`;
  };

  const handleStartTime = (checkStatus) => {
    const startTime = checkStatus ? currentTime : 0;
    setStartHereTime(startTime);
    const mediaDataWithEmbedCode = addEmbedCodeToMediaData({
      mediaData: mediaData,
      startHere: startTime,
    });
    setMediaData(mediaDataWithEmbedCode);
    setChecked(checkStatus);
  };

  const handlePlay = (blockType) => {
    handleStartTime(false);
    blockType === "Audio" ? setAudioPlaying(true) : setVideoPlaying(true);
  };

  const handlePause = (type) => {
    type === "audio" ? setAudioPlaying(false) : setVideoPlaying(false);
  };

  const renderMediaData = () => {
    let mediaDataArray = [];
    // Establishes the number of tab rows and tabs for each row
    const tabRowsLengths = getTabRowsLengths(mediaData.length);
    let tabRowsIndex = 0;
    let tabRowLength = tabRowsLengths[tabRowsIndex];
    for (let i = 0; i < mediaData.length; i += tabRowLength) {
      const mediaDataChunk = mediaData.slice(i, i + tabRowLength);
      mediaDataArray.push(
        <Tab.List key={i} className="flex bg-blue-900/20 p-1">
          {mediaDataChunk.map((media, index) => (
            <Tab
              key={media.id}
              className={({ selected }) =>
                classNames(
                  `w-full ${
                    mediaData.length === 1
                      ? "rounded-t-xl"
                      : i === 0 && index === 0
                      ? "rounded-tl-xl"
                      : i === 0 && index === mediaDataChunk.length - 1
                      ? "rounded-tr-xl"
                      : "no rounded"
                  } h-6 lg:h-10 py-1 lg:py-2.5 text-xs lg:text-sm font-medium`,
                  selected
                    ? "bg-white text-blue-700 shadow"
                    : "text-blue-100 hover:bg-white/[0.12] hover:text-white"
                )
              }
            >
              {index + 1 + i}-{media.type}
            </Tab>
          ))}
        </Tab.List>
      );
      tabRowLength = tabRowsLengths[tabRowsIndex + 1];
    }
    return mediaDataArray;
  };

  function getTabRowsLengths(length) {
    const maxPerRow = 5;
    const rows = Math.ceil(length / maxPerRow);
    // Get the minimum number of tabs per row
    const minTabs = Math.floor(length / rows);
    const remainder = length % rows;
    const tabRowsLengths = [];
    for (let i = 0; i < rows; i++) {
      // Distribute the remainder one per row until it runs out
      const tabRowLength = i < remainder ? minTabs + 1 : minTabs;
      tabRowsLengths.push(tabRowLength);
    }
    return tabRowsLengths;
  }

  return (
    <div className="flex justify-center items-center w-8 h-8 bg-gray-300 border-[1px] border-gray-400 rounded-full">
      <button onClick={() => openModal()}>
        <CodeBracketIcon className="w-6 h-6 text-gray-600" />
      </button>

      <Transition appear show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[100]"
          onClose={() => setOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="transform overflow-hidden rounded-2xl text-left align-middle shadow-xl transition-all">
                  <Tab.Group onChange={() => openModal()}>
                    <div className="bg-white">{renderMediaData()}</div>
                    <Tab.Panels className="">
                      {mediaData.length > 0 &&
                        mediaData.map((media) => (
                          <Tab.Panel
                            key={media.id}
                            className={classNames(
                              "ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2"
                            )}
                          >
                            <div className="lg:flex">
                              {media.type === "Video" ? (
                                <CustomVideoPlayer
                                  handleStartHereTime={handleStartHereTime}
                                  checked={checked}
                                  addEmbedCodeToMediaData={
                                    addEmbedCodeToMediaData
                                  }
                                  mediaData={mediaData}
                                  setMediaData={setMediaData}
                                  modal={true}
                                  videoUrl={media.url}
                                  handlePause={() => handlePause("video")}
                                  handlePlay={() => handlePlay("Video")}
                                />
                              ) : (
                                <CustomAudioPlayer
                                  handleStartHereTime={handleStartHereTime}
                                  checked={checked}
                                  addEmbedCodeToMediaData={
                                    addEmbedCodeToMediaData
                                  }
                                  mediaData={mediaData}
                                  setMediaData={setMediaData}
                                  modal={true}
                                  audioUrl={media.url}
                                  handlePause={() => handlePause("audio")}
                                  handlePlay={() => handlePlay("Audio")}
                                />
                              )}
                              <div className="w-[328px] lg:w-[384px] px-3 py-2 lg:p-4 bg-white">
                                <Dialog.Title
                                  as="h3"
                                  className="text-lg font-medium leading-6 text-gray-900"
                                >
                                  Embed Code
                                </Dialog.Title>
                                <div className="">
                                  <p className="text-sm text-gray-500 break-words h-[140px] lg:h-[181px] mb-2 lg:mb-4 overflow-auto">
                                    {media.embedCode}
                                  </p>
                                </div>
                                <div>
                                  <div className="flex items-center">
                                    <input
                                      disabled={
                                        currentTime === 0 ||
                                        (media.type === "Video" &&
                                          videoPlaying) ||
                                        (media.type === "Audio" && audioPlaying)
                                      }
                                      onChange={() => handleStartTime(!checked)}
                                      type="checkbox"
                                      checked={checked}
                                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                    />
                                    <label className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300">
                                      Start here: {formatTime(startHereTime)}
                                    </label>
                                  </div>
                                </div>
                                <div className="mt-2 lg:mt-4 flex items-center lg:block">
                                  <button
                                    disabled={copyDisabled}
                                    type="button"
                                    className={`mr-2 py-1 px-3 inline-flex items-center justify-center rounded-md border border-transparent text-sm leading-4 lg:leading-5 font-medium bg-blue-100 ${
                                      copyDisabled
                                        ? " bg-blue-50 text-blue-200"
                                        : "text-blue-900 hover:bg-blue-200"
                                    } focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2`}
                                    onClick={() => handleCopy(media.embedCode)}
                                  >
                                    <span>Copy</span>
                                  </button>
                                  <div className="lg:mt-2 h-5">
                                    {" "}
                                    {copyStatus.copySuccess ? (
                                      <span className="w-fit px-1 flex text-sm bg-green-100 text-green-700">
                                        <CheckIcon className="mr-1" />
                                        Copied!
                                      </span>
                                    ) : copyStatus.copyFail ? (
                                      <span className="w-fit px-1 flex text-sm text-red-600 ">
                                        <XMarkIcon className="w-5 h-5 mr-1" />
                                        Copy Failed!
                                      </span>
                                    ) : (
                                      <span></span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Tab.Panel>
                        ))}
                    </Tab.Panels>
                  </Tab.Group>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

export default EmbedClass;
